import React, { useEffect } from "react"
import useLoader from "../../../../shared/components/loading-dialog/useLoader.hook";
import useReportingAndAnalyticsApi from "../../../pages/reporting-and-analytics/useReportingAndAnalyticsApi";
import LoadingDialogComponent from "../../../../shared/components/loading-dialog/loading-dialog.component";
import useWizardReportDataContext from "../contexts/useWizardReportData.context";
import { IReportDataOptions, IReportDataOptionsResponse } from "../../../../shared/contracts/IReportDataOptions";


/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @createdOn Jan/05/2025
 */
export function useGetOptionsApi() {

    const initReportDataOptions: IReportDataOptions = {
        options: [],
        stringOptions: [],
        dateOptions: [],
        numberOptions: [],
        filterOperatorValues: [],
    }

    const { getFilterOptions, getGroupByOptions, getFormatOptions } = useReportingAndAnalyticsApi();
    const {
        filterOptions, setFilterOptions,
        groupByOptions, setGroupByOptions,
        formatOptions, setFormatOptions,
    } = useWizardReportDataContext();

    useEffect(() => {
        if (!filterOptions) {
            setFilterOptions({ ...initReportDataOptions });
            filterOptionsLoader.loadAsync();
        }
    }, [filterOptions])

    useEffect(() => {
        if (!groupByOptions) {
            setGroupByOptions({ ...initReportDataOptions });
            groupByOptionsLoader.loadAsync();
        }
    }, [groupByOptions])

    useEffect(() => {
        if (!formatOptions) {
            setFormatOptions({ ...initReportDataOptions });
            formatOptionsLoader.loadAsync();
        }
    }, [formatOptions])


    // TODO: discuss with naval why groupBy function is using optionId instead of categoryId 
    const transformByOptionsId = (data: IReportDataOptionsResponse): IReportDataOptions => {
        return {
            options: data.options,
            stringOptions: data.options.filter(x => data.stringOptions.includes(x.optionId)),
            dateOptions: data.options.filter(x => data.dateOptions.includes(x.optionId)),
            numberOptions: data.options.filter(x => data.numberOptions.includes(x.optionId)),
            filterOperatorValues: Array.isArray(data.filterOperatorValues) ? [...data.filterOperatorValues] : []
        } as IReportDataOptions;
    }


    const transformByCategoryId = (data: IReportDataOptionsResponse): IReportDataOptions => {
        return {
            options: data.options,
            stringOptions: data.options.filter(x => data.stringOptions.includes(x.categoryId)),
            dateOptions: data.options.filter(x => data.dateOptions.includes(x.categoryId)),
            numberOptions: data.options.filter(x => data.numberOptions.includes(x.categoryId)),
            filterOperatorValues: Array.isArray(data.filterOperatorValues) ? [...data.filterOperatorValues] : []
        } as IReportDataOptions;
    }


    const groupByOptionsLoader = useLoader({
        loaderName: "GroupBy Options",
        loadAsync: () =>
            getGroupByOptions(
                {
                    doIf: () => groupByOptionsLoader.isNotLoading,
                    doBefore: () => {
                        setGroupByOptions({ ...initReportDataOptions });
                        groupByOptionsLoader.SET_LOADING("Fetching GroupBy Options...");
                    },
                    onFulfilled: (data) => {
                        setGroupByOptions(transformByOptionsId(data));
                        groupByOptionsLoader.SET_SUCCESS("Success.!");
                    },
                    onException: groupByOptionsLoader.SET_EXCEPTION,
                }
            ),
    });

    const filterOptionsLoader = useLoader({
        loaderName: "Filter Options",
        loadAsync: () =>
            getFilterOptions(
                {
                    doIf: () => filterOptionsLoader.isNotLoading,
                    doBefore: () => {
                        setFilterOptions({ ...initReportDataOptions });
                        filterOptionsLoader.SET_LOADING("Fetching Filter Options...");
                    },
                    onFulfilled: (data) => {
                        setFilterOptions(transformByCategoryId(data));
                        filterOptionsLoader.SET_SUCCESS("Success.!");
                    },
                    onException: filterOptionsLoader.SET_EXCEPTION,
                }
            ),
    });

    const formatOptionsLoader = useLoader({
        loaderName: "Format Options",
        loadAsync: () =>
            getFormatOptions(
                {
                    doIf: () => formatOptionsLoader.isNotLoading,
                    doBefore: () => {
                        setFormatOptions({ ...initReportDataOptions });
                        formatOptionsLoader.SET_LOADING("Fetching Format Options...");
                    },
                    onFulfilled: (data) => {
                        setFormatOptions(transformByCategoryId(data));
                        formatOptionsLoader.SET_SUCCESS("Success.!");
                    },
                    onException: formatOptionsLoader.SET_EXCEPTION,
                }
            ),
    });


    const GroupByOptionsProgressIndicator = () => <LoadingDialogComponent loaderHook={groupByOptionsLoader} />
    const FilterOptionsProgressIndicator = () => <LoadingDialogComponent loaderHook={filterOptionsLoader} />
    const FormatOptionsProgressIndicator = () => <LoadingDialogComponent loaderHook={formatOptionsLoader} />

    // ---
    return {
        GroupByOptionsProgressIndicator,
        FilterOptionsProgressIndicator,
        FormatOptionsProgressIndicator,

        filterOptionsLoader,
        groupByOptionsLoader,
        formatOptionsLoader,
    }
}